/* eslint-disable camelcase */

import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import {
  adminCreateLessonHistoryAdminNote,
  adminDeleteLessonHistoryAdminNote,
  adminGetLessonHistoryById,
  adminLessonHistoryListGet,
} from './adminApi';
import { RootState } from '../..';
import { getLessonHistoryById, lessonHistoryListGet } from './api';
import { ISection } from '../SectionSlice';
import { ICourse } from '../CoursesSlice';
import { IUser, TIdentifiedUserRole } from '../../UserSlice';

export interface ILessonHistoryParticipantAttendance {
  id: string;
  user_id: string;
  name: string;
  user_email: string;
  join_time: string;
  leave_time: string;
  duration: number;
  attentiveness_score: string;
  failover: boolean;
  status: string;
  groupId: string;
  customer_key: string;
  participant_user_id: string;
}

export interface ILessonHistoryAdminNote {
  _id: string;
  description: string;
  createdBy: string | IUser;
  createdAt: string;
}

export interface ILessonHistory {
  _id: string;
  section: string | ISection;
  course: string | ICourse;
  subject: string;
  curriculum: string;
  grade: string;
  start: string;
  createdAt: string;
  students: IUser[];
  tutors: IUser[];
  end?: string;
  identifiedUserRole?: TIdentifiedUserRole;
  attendanceHistory?: ILessonHistoryParticipantAttendance[];
  adminNotes?: ILessonHistoryAdminNote[];
  eventId?: string;
  studentJoinTime?: number;
  tutorJoinTime?: number;
  paymentTutor?: string | IUser;
}

export interface IInitialLessonHistoryState {
  status: 'idle' | 'loading' | 'rejected';
  lessonHistoryListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonHistory>;
  };
  lessonHistoryData?: ILessonHistory;
  // ADMIN
  adminLessonHistoryListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonHistory>;
  };
  adminLessonHistoryData?: ILessonHistory;
}

const initialState: IInitialLessonHistoryState = {
  status: 'idle',
  lessonHistoryListData: undefined,
  lessonHistoryData: undefined,
  // ADMIN
  adminLessonHistoryListData: undefined,
  adminLessonHistoryData: undefined,
};

export const LessonHistorySlice = createSlice({
  name: 'lessonHistory',
  initialState,
  reducers: {
    resetLessonHistoryData: (state) => {
      state.lessonHistoryData = undefined;
    },
    resetLessonHistoryListData: (state) => {
      state.lessonHistoryListData = undefined;
    },
    // ADMIN
    resetAdminLessonHistoryListData: (state) => {
      state.adminLessonHistoryListData = undefined;
    },
    resetAdminLessonHistoryData: (state) => {
      state.adminLessonHistoryData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(lessonHistoryListGet.fulfilled, (state, action) => {
        const result = action.payload;

        const newLessonHistoryListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.lessonHistoryListData = newLessonHistoryListData;
      })
      .addCase(getLessonHistoryById.fulfilled, (state, action) => {
        const result = action.payload;

        state.lessonHistoryData = result;
      });

    // Admin
    builder
      .addCase(adminLessonHistoryListGet.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminLessonHistoryListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminLessonHistoryListData = newAdminLessonHistoryListData;
      })
      .addCase(adminGetLessonHistoryById.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;
      })
      .addCase(adminCreateLessonHistoryAdminNote.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;
      })
      .addCase(adminDeleteLessonHistoryAdminNote.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        lessonHistoryListGet,
        getLessonHistoryById,
        // ADMIN
        adminLessonHistoryListGet,
        adminGetLessonHistoryById,
        adminCreateLessonHistoryAdminNote,
        adminDeleteLessonHistoryAdminNote
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        lessonHistoryListGet,
        getLessonHistoryById,
        // Admin
        adminLessonHistoryListGet,
        adminGetLessonHistoryById,
        adminCreateLessonHistoryAdminNote,
        adminDeleteLessonHistoryAdminNote
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        lessonHistoryListGet,
        getLessonHistoryById,
        // ADMIN
        adminLessonHistoryListGet,
        adminGetLessonHistoryById,
        adminCreateLessonHistoryAdminNote,
        adminDeleteLessonHistoryAdminNote
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getLessonHistoryState = (
  state: RootState
): RootState['lessonHistory'] => state.lessonHistory;

export const {
  resetAdminLessonHistoryListData,
  resetLessonHistoryListData,
  resetAdminLessonHistoryData,
  resetLessonHistoryData,
} = LessonHistorySlice.actions;

export * from './api';
export * from './adminApi';
export default LessonHistorySlice.reducer;
